/* eslint-disable camelcase */
import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { TextInputControl, Submit, Legend, Fieldset, Alert, Checkbox, Tooltip } from '@ver-uds/uswds-react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from '@ver-uds/react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Application, AuthorizationResponse, UserStatus } from '@vis-auth/vis-user-client';
import { Client } from '../components/Providers/OAuthParamsParser';
import { useUserData, UserData } from '../components/Providers/UserDataProvider';
import UseAgreementModal from '../components/Modals/UseAgreementModal';
import PrivacyActModal from '../components/Modals/PrivacyActModal';
import PaperworkReductionActModal from '../components/Modals/PaperworkReductionActModal';
import { oidcApi } from '../userService';
import DHSFormWrapper from '../components/DHSFormWrapper/DHSFormWrapper';
import mapErrors from '../map-errors';
import { useEnvironment, IsExternaltest } from '../components/Environment/EnvironmentContext';
import IEDeprecationAlert from '../components/IEDeprecationAlert/IEDeprecationAlert';
import { useFeatureFlags } from '../components/FeatureFlags/FeatureFlagsContext';
import EveOutageWarning from './eve-outage-warning';
import LoginDotGovLogo from '../images/loginDotGovLogo_blue.svg';

declare global {
  interface Document {
    documentMode?: number;
  }
}

export function getBrandName(client: string): string {
  if (client === Client.EVERIFY) {
    return 'E-Verify';
  }

  if (client === Client.SAVE) {
    return 'SAVE';
  }

  return client;
}

export function isValidDhsGovUri(uri: string): boolean {
  try {
    const url = new URL(uri);
    return url.protocol === 'https:' && url.hostname.endsWith('.gov');
  } catch {
    return false;
  }
}
async function loginPost({
  username,
  password,
  params,
}: {
  username: string;
  password: string;
  params: UserData;
}): Promise<AuthorizationResponse> {
  const { client_id, code_challenge, code_challenge_method, scope, state, redirect_uri } = params;

  try {
    const response = await oidcApi.authorize({
      clientId: client_id,
      redirectUri: redirect_uri,
      codeChallenge: code_challenge,
      codeChallengeMethod: code_challenge_method,
      username,
      password,
      scope,
      state,
    });

    return response;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('error', err);
    // The error caught is a Response type object, not an Error. Re-throw a true Error.
    throw new Error(await mapErrors(err as Response, true));
  }
}

const LegalContent = ({ client }: { client: string }): JSX.Element => {
  return (
    <ul className="margin-top-3 add-list-reset">
      {client === Client.EVERIFY || client === Client.EVEPLUS || client === Client.MYEVE ? (
        <>
          <li>
            <PaperworkReductionActModal app={Application.EVERIFY} />
          </li>
          <li>
            OMB Control No. 1615-0092 <br />
            Expiration Date 5/31/2027
          </li>
        </>
      ) : (
        <>
          <li>
            <PaperworkReductionActModal app={Application.SAVE} />
          </li>
          <li>
            <PrivacyActModal />
          </li>
        </>
      )}
    </ul>
  );
};

export const getLoginDotGovHref = (redirect_uri: string): string => {
  return `${redirect_uri}/loginDotGov`;
};

export const getError = (errorMessage: string, redirect_uri: string, client: string): string | JSX.Element => {
  if (errorMessage.includes('Unable to log in')) {
    return (
      <span>
        {errorMessage}
        <a href={redirect_uri}>Return to the previous site</a> to try again.
      </span>
    );
  }
  if (errorMessage.includes('You do not have access')) {
    return errorMessage.replaceAll('{{app}}', getBrandName(client));
  }
  if (errorMessage.includes('Your account has already been migrated')) {
    return (
      <span>
        {errorMessage}
        <a href={getLoginDotGovHref(redirect_uri)}>Login.gov</a>
      </span>
    );
  }
  return errorMessage;
};

const Login = (): JSX.Element => {
  const [showPassword, setShowPassword] = useState(false);
  const alertElement = useRef<HTMLInputElement>(null);
  const IERef = useRef<HTMLDivElement>(null);
  const [showAlert, setShowAlert] = useState(false);
  const history = useHistory();
  const env = useEnvironment();
  const { showEveOutageBanner, isLoginDotGovEnabledSAVE, isLoginDotGovEnabledEVerify } = useFeatureFlags();
  const [showSaveLegacyLogin, setShowSaveLegacyLogin] = useState(false);

  const userData = useUserData();

  const {
    client,
    redirect_uri,
    state,
    authorizationCode,
    setUserStatus,
    setUserId,
    setLogonId,
    setAuthorizationCode,
    calcDaysToPasswordExpire,
  } = userData;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<{ username: string; password: string }>({
    reValidateMode: 'onSubmit',
  });
  const [username, password] = watch(['username', 'password']);

  const [mutate, { status, isIdle: isLoggingInIdle, error }] = useMutation(loginPost, {
    onSuccess: (response: AuthorizationResponse) => {
      if (response?.user?.status === UserStatus.LOCKED) {
        setUserId(response.user.id);
        setLogonId(response.user.logonId);
        setUserStatus(UserStatus.LOCKED);
        history.push(`/account-locked/user-id`);
        return;
      }

      if (response?.user?.roles.includes('WEB_SERVICE')) {
        throw new Error(
          'Web Service user accounts cannot login to this application. Try using a different user account to login.',
        );
      }

      if (response.authorizationCode) {
        setAuthorizationCode(response.authorizationCode);
      }

      if (response.user) {
        setUserId(response.user.id);
        setLogonId(response?.user.logonId);
      }

      if (response.user?.status === UserStatus.DEACTIVATED) {
        setUserStatus(UserStatus.DEACTIVATED);
        history.push(`/account-locked/deactivated`);
        return;
      }

      let daysToExpire = -1;
      if (response.user?.passwordExpirationDate) {
        daysToExpire = calcDaysToPasswordExpire(response.user.passwordExpirationDate);
      }

      if (
        response.user?.status === UserStatus.PASSWORDCHANGEREQUIRED ||
        response.user?.status === UserStatus.PENDINGREACTIVATION
      ) {
        setUserStatus(UserStatus.PASSWORDCHANGEREQUIRED);
        history.push('/password-expired/change-password');
        return;
      }

      // need to check that date is not in the past (already expired) and within the next 7 days, otherwise login
      if (daysToExpire > 0 && daysToExpire <= 7) {
        history.push('/password-expiring');
      } else {
        window.location.replace(`${redirect_uri}?code=${response.authorizationCode}&state=${state}`);
      }
    },
  });

  let errorMessage: string | JSX.Element | null = '';

  if (error instanceof Error) {
    errorMessage = getError(error.message, redirect_uri, client);
  }

  const clientIsEverify = client === Client.EVERIFY;
  const clientIsSave = client === Client.SAVE;
  const clientIsScl = client === Client.SCL;
  const clientIsEveplus = client === Client.EVEPLUS;
  const clientIsMyEve = client === Client.MYEVE;

  // If we are not attempting to login and there is an authorization code already present for this session
  // bail out to the redirect URI because the code challenge has been consumed.
  if (isLoggingInIdle && authorizationCode) {
    window.location.replace(redirect_uri);
  }

  const getEnrollmentHref = (): string => {
    let enrollmentHref = env.NEW_EVE_ENROLLMENT_ENABLED ? `/enroll/${client.toLowerCase()}` : env.EVERIFY_ENROLL;
    if (clientIsEverify && isLoginDotGovEnabledEVerify) {
      enrollmentHref = env.EVERIFY_MFA_ENROLL;
    }
    return enrollmentHref;
  };

  const toggleSaveLegacyLoginForm = (): void => {
    setShowSaveLegacyLogin(!showSaveLegacyLogin);
  };

  const showEveAlert = (condition: boolean): boolean => {
    return clientIsEverify && condition;
  };

  const showLoginErrorMessage = (clientEnum: string, message: string | undefined): string | undefined | boolean => {
    return client === clientEnum && message;
  };

  const showLDGBanner = (): boolean => {
    return (
      (clientIsEverify && isLoginDotGovEnabledEVerify) ||
      (clientIsSave && isLoginDotGovEnabledSAVE) ||
      clientIsEveplus ||
      clientIsMyEve
    );
  };

  function handleLoginDotGovClick(originalUri: string, redirectUri: string) {
    if (!isValidDhsGovUri(originalUri) || !isValidDhsGovUri(redirectUri)) {
      alert('Invalid redirect. Both URLs must use HTTPS and contain dhs.gov.');
      return;
    }
    window.location.replace(getLoginDotGovHref(redirectUri));
  }

  return (
    <>
      <Helmet>
        <title>Sign In</title>
      </Helmet>
      <UseAgreementModal />
      <div className="grid-col" style={{ paddingLeft: '0', paddingRight: '0' }}>
        {showEveAlert(showEveOutageBanner) ? <EveOutageWarning /> : null}
        {showLDGBanner() && (
          <div className="margin-x-auto grid-col tablet:grid-col-7 desktop:grid-col-6 radius-md margin-bottom-3">
            <Alert status="info">
              <Alert.Heading>New! Sign in with Login.gov</Alert.Heading>
              <Alert.Text>
                Login.gov is a sign in service that offers secure and private access to{' '}
                {clientIsEverify && (
                  <>
                    {'E-Verify. '}
                    <a href="https://www.e-verify.gov/access-e-verify-with-logingov">
                      Learn more about E-Verify&#39;s integration with Login.gov
                    </a>
                  </>
                )}
                {clientIsSave && (
                  <>
                    {'SAVE. '}
                    <a href="https://www.uscis.gov/save/access-save-with-logingov">
                      Learn more about SAVE&#39;s integration with Login.gov
                    </a>
                  </>
                )}
                {clientIsEveplus && (
                  <>
                    {'Everify+. '}
                    <a href="https://www.e-verify.gov/access-e-verify-with-logingov">
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit
                    </a>
                  </>
                )}
                {clientIsMyEve && (
                  <>
                    {'myE-Verify. '}
                    <a href="https://www.e-verify.gov/access-e-verify-with-logingov">
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit
                    </a>
                  </>
                )}
              </Alert.Text>
            </Alert>
          </div>
        )}
        <DHSFormWrapper>
          <form
            noValidate
            aria-label="Sign In"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(async (): Promise<void> => {
              const isIE = !!document.documentMode;

              if (isIE && clientIsEverify) {
                setShowAlert(true);
                IERef?.current?.focus();
              } else {
                await mutate({ username, password, params: userData });

                if (alertElement.current) {
                  alertElement.current.focus();
                }
              }
            })}
          >
            <Fieldset>
              <Legend>
                <h2>Sign In</h2>
              </Legend>
              {showLDGBanner() && (
                <>
                  <div className="margin-top-4 margin-bottom-4">
                    <Button
                      className="margin-top-1 width-full text-black"
                      type="button"
                      variant="secondary"
                      // fix this onclick. on click go to a function that checks the original uri and the redirect uri. make sure its https. has dhs.gov.
                      // redirect is also https and has dhs.gov on it.
                      onClick={() => handleLoginDotGovClick(window.location.href, redirect_uri)}
                    >
                      Sign In with <span className="usa-sr-only">Login.Gov</span>{' '}
                      <img src={LoginDotGovLogo} alt="" className="margin-left-1" />
                    </Button>
                  </div>
                  <hr />
                </>
              )}
              {clientIsSave && IsExternaltest() && (
                <div className="margin-top-4 margin-bottom-4">
                  <h4> Are you an SSA user?</h4>
                  <p>
                    {' '}
                    If you are an SSA user who has already authenticated with MockISAM, click the button below to
                    display the login fields.
                  </p>
                  <Checkbox
                    onClick={toggleSaveLegacyLoginForm}
                    id="showLegacyLoginSaveExttestCheckbox"
                    name="showLegacyLoginSaveExttestCheckbox"
                    label="Show Legacy Login Fields"
                  />
                </div>
              )}

              {((IsExternaltest() && client && showSaveLegacyLogin) || clientIsScl) && (
                <>
                  <p>
                    If you need help remembering or resetting your User ID or your password, reach out to your Super
                    User contact for assistance.
                  </p>
                  <TextInputControl
                    id="username"
                    className="margin-bottom-1"
                    label="User ID"
                    required
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...register('username', {
                      required: 'User ID is required',
                      minLength: {
                        value: 8,
                        message: 'Must be exactly 8 alpha-numeric characters',
                      },
                    })}
                    errorMessage={errors.username && errors.username.message}
                    errorId="username-error"
                    maxLength={8}
                    minLength={8}
                  />
                  <TextInputControl
                    id="password"
                    className="margin-bottom-1"
                    label="Password"
                    required
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...register('password', { required: 'Password is required' })}
                    errorMessage={errors.password && errors.password.message}
                    errorId="password-error"
                    type={showPassword ? 'text' : 'password'}
                  />
                  <div
                    className="display-flex flex-justify margin-bottom-3
                  "
                  >
                    <Button
                      type="button"
                      variant="tertiary"
                      className="margin-top-05 margin-left-2 text-right"
                      aria-controls="password"
                      onClick={(): void => setShowPassword(!showPassword)}
                    >
                      {showPassword ? 'Hide password' : 'Show password'}
                    </Button>
                  </div>
                </>
              )}
              {showLoginErrorMessage(Client.EVERIFY, env.EVERIFY_LOGIN_ERROR_MESSAGE) ? (
                <Alert status="error">
                  <Alert.Text>{env.EVERIFY_LOGIN_ERROR_MESSAGE}</Alert.Text>
                </Alert>
              ) : null}
              {showLoginErrorMessage(Client.SAVE, env.SAVE_LOGIN_ERROR_MESSAGE) ? (
                <Alert status="error">
                  <Alert.Text>{env.SAVE_LOGIN_ERROR_MESSAGE}</Alert.Text>
                </Alert>
              ) : null}
              {showEveAlert(showAlert) ? (
                <div id="ie-deprecation-alert" tabIndex={-1} ref={IERef}>
                  <IEDeprecationAlert showAlert={showAlert} />
                </div>
              ) : null}
              {errorMessage ? (
                <div id="sign-in-error" className="margin-y-3" tabIndex={-1} ref={alertElement}>
                  <Alert status="error">
                    <Alert.Text>{errorMessage}</Alert.Text>
                  </Alert>
                </div>
              ) : null}

              {clientIsEverify && (
                <div>
                  <hr />
                  <h4>Want to participate in E-Verify?</h4>
                  <p>
                    If your company does not have an account, you can <a href={getEnrollmentHref()}>enroll today!</a>
                  </p>
                </div>
              )}

              {clientIsSave && IsExternaltest() && showSaveLegacyLogin && (
                <Submit
                  className="margin-top-4 margin-bottom-4 width-full"
                  value="Sign In"
                  disabled={status === 'loading'}
                />
              )}
              {clientIsSave && env.SAVE_SSA_LOGON && (
                <div>
                  <hr />
                  <h4>SSA Employee?</h4>
                  <p>
                    If you are an employee of the Social Security Administration,{' '}
                    <a href={env.SAVE_SSA_LOGON}>click here to sign in to SAVE.</a>
                  </p>
                </div>
              )}

              {/* {clientIsEveplus && (
                <div>
                  <hr />
                  <h4>Want to participate in E-Verify+?</h4>
                  <p>
                    If you do not currently have an account, you can sign up <a>here</a>.
                  </p>
                </div>
              )} */}
            </Fieldset>
          </form>

          {!clientIsEveplus && !clientIsMyEve && <hr className="margin-top-4" />}
          <h3>Legal</h3>
          <LegalContent client={client} />
        </DHSFormWrapper>
      </div>
    </>
  );
};

export default Login;
